import React, { useEffect, useRef, useState } from 'react';
import { useToast } from '@chakra-ui/react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { TARM_AWS_URL } from '../../api/api';
import Loading from '../Loading/Loading';
import { Result } from 'antd';
import RankingResults from '../Common/Ranking/RankingResults';
import ProgressSteps from './ProgressSteps';
import BatchOperationProgressSteps from './BatchOperationProgressSteps';

const BatchOperationProgress = () => {
  const { author_email, run_id } = useParams();
  const [timeStamp, setTimeStamp] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const intervalRef = useRef();
  const toast = useToast();

  // Create a cancel token source for axios requests
  const source = useRef(axios.CancelToken.source()).current;

  // Fetch status once and set up polling
  useEffect(() => {
    fetchStatusOnce(author_email, run_id);
    const interval = setInterval(
      () => fetchStatus(author_email, run_id),
      10000
    );
    intervalRef.current = interval;

    return () => {
      clearInterval(intervalRef.current);
      source.cancel(); // Cancel any ongoing requests
    };
  }, [author_email, run_id, source]);

  const fetchStatusOnce = async (author_email, run_id) => {
    try {
      const response = await TARM_AWS_URL.get(
        `get-status?author_email=${author_email}&run_id=${run_id}`,
        { cancelToken: source.token }
      );
      setTimeStamp(response.data);
    } catch (error) {
      handleError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchStatus = async (author_email, run_id) => {
    try {
      const response = await TARM_AWS_URL.get(
        `get-status?author_email=${author_email}&run_id=${run_id}`,
        { cancelToken: source.token }
      );
      const newTimeStamp = response.data;
      setTimeStamp(newTimeStamp);

      // Stop polling if the status is terminal
      if (
        ['SUCCEEDED', 'FAILED', 'TIMED_OUT', 'ABORTED'].includes(
          newTimeStamp.batch_op_status
        )
      ) {
        clearInterval(intervalRef.current);
      }
    } catch (error) {
      handleError(error);
    }
  };

  const handleError = (error) => {
    console.error(error);
    // Stop pooling of error
    clearInterval(intervalRef.current);
    toast({
      title: error.message,
      status: 'error',
      duration: 3000,
      isClosable: true,
    });
  };

  if (isLoading) {
    return <Loading loading={isLoading} />;
  }

  if (!timeStamp?.batch_op_status) {
    return (
      <Result
        status="500"
        title="500"
        subTitle="Sorry, something went wrong."
      />
    );
  }

  return (
    <>
      {timeStamp.hasOwnProperty('jds') ? (
        <>
          {timeStamp?.batch_op_status === 'SUCCEEDED' ? (
            <RankingResults
              author_email={author_email}
              run_id={run_id}
              timeStamp={timeStamp}
              API_URL={TARM_AWS_URL}
              heading="Batch Operation Details"
            />
          ) : (
            <BatchOperationProgressSteps timeStamp={timeStamp} />
          )}
        </>
      ) : (
        <ProgressSteps />
      )}
    </>
  );
};

export default BatchOperationProgress;
